import {
	ArrowLeftOutlined,
	CalendarOutlined,
	ClockCircleOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { Avatar, Button, Card, Descriptions, Skeleton, Typography } from "antd";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMe } from "@/modules/auth/hooks/useAuth";
import { StatusTag } from "@/shared/components/StatusTag";
import { resolveImageUrl } from "@/shared/utils/resolveImageUrl";
import { StaffFormModal } from "../components/StaffFormModal";
import { useStaff } from "../hooks/useStaff";
import { useStaffStore } from "../store/staff.store";
import type { Staff, StaffType } from "../types";
import { formatWorkdaysDisplay } from "../utils/formatWorkdays";

const STAFF_TYPE_LABELS: Record<StaffType, string> = {
	EMPLOYEE: "Xodim",
	BRIGADIER: "Brigadir",
	SECURITY: "Xavfsizlik",
	ADMIN: "Admin",
	MANAGER: "Menejer",
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: detail page with multiple conditional sections
export function StaffDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const { data: staff, isLoading } = useStaff(id ?? "");
	const { data: meUser } = useMe();
	const openEditModal = useStaffStore((s) => s.openEditModal);
	const organizations = meUser?.organizations ?? [];

	if (!id) {
		return <Navigate to="/organizations" replace />;
	}

	const photoUrl = (staff as Staff & { photo?: { url?: string } })?.photo?.url ?? staff?.photoUrl;
	const resolvedPhotoUrl = resolveImageUrl(photoUrl);

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<Button
						type="text"
						icon={<ArrowLeftOutlined />}
						onClick={() => navigate(-1)}
						size="small"
						className="text-ink-soft"
					/>
					{isLoading ? (
						<Skeleton.Input active size="small" style={{ width: 180 }} />
					) : (
						<h4 className="text-lg font-semibold m-0">{staff?.fullname ?? "Xodim"}</h4>
					)}
				</div>
				{staff && (
					<Button type="primary" onClick={() => openEditModal(staff)}>
						Tahrirlash
					</Button>
				)}
			</div>

			{isLoading ? (
				<Card size="small">
					<Skeleton active paragraph={{ rows: 4 }} />
				</Card>
			) : staff ? (
				<>
					<Card size="small">
						<div className="flex flex-col sm:flex-row gap-4 items-start">
							<div className="shrink-0">
								{resolvedPhotoUrl ? (
									<Avatar
										size={64}
										src={resolvedPhotoUrl}
										style={{ background: "#1D2026", color: "#fff", fontSize: 24 }}
									>
										{staff.fullname?.charAt(0)?.toUpperCase() ?? "X"}
									</Avatar>
								) : (
									<Avatar size={64} style={{ background: "#1D2026", color: "#fff", fontSize: 24 }}>
										{staff.fullname?.charAt(0)?.toUpperCase() ?? "X"}
									</Avatar>
								)}
							</div>
							<div className="flex-1 min-w-0">
								<Descriptions
									size="small"
									column={1}
									items={[
										{
											key: "fullname",
											label: (
												<span className="flex items-center gap-1">
													<UserOutlined /> F.I.SH
												</span>
											),
											children: staff.fullname,
										},
										{
											key: "staffType",
											label: "Xodim turi",
											children: STAFF_TYPE_LABELS[staff.staffType ?? "EMPLOYEE"] ?? staff.staffType,
										},
										{
											key: "phone",
											label: "Telefon",
											children: staff.phoneNumber ? (
												<span className="font-mono">{staff.phoneNumber}</span>
											) : (
												"—"
											),
										},
										{
											key: "jobEntryDate",
											label: (
												<span className="flex items-center gap-1">
													<CalendarOutlined /> Ishga kirgan sana
												</span>
											),
											children: staff.jobEntryDate
												? new Date(staff.jobEntryDate).toLocaleDateString("uz-UZ", {
														year: "numeric",
														month: "long",
														day: "numeric",
													})
												: "—",
										},
										{
											key: "hours",
											label: "Soat (hafta/kun)",
											children: (
												<span>
													{staff.maxHoursPerWeek} / {staff.maxHoursPerDay}
												</span>
											),
										},
										{
											key: "isActive",
											label: "Holat",
											children: staff.isActive ? (
												<StatusTag tone="success">Faol</StatusTag>
											) : (
												<StatusTag tone="default">Nofaol</StatusTag>
											),
										},
									]}
								/>
							</div>
						</div>
					</Card>

					{staff.workdays && staff.workdays.length > 0 && (
						<Card
							size="small"
							title={
								<span className="flex items-center gap-2">
									<ClockCircleOutlined /> Ish vaqti
								</span>
							}
						>
							<Typography.Text>{formatWorkdaysDisplay(staff.workdays)}</Typography.Text>
						</Card>
					)}
				</>
			) : (
				<Card size="small">
					<Typography.Text type="secondary">Xodim topilmadi</Typography.Text>
				</Card>
			)}

			{staff && (
				<StaffFormModal
					organizationId={staff.organizationId}
					organizations={organizations}
					brigadeId={staff.brigadeId ?? undefined}
				/>
			)}
		</div>
	);
}
